/**
 * roadmap.service — callers de las RPC del roadmap de pareja.
 *
 * Feature 1 (docs/plan-ruta-terapeutica-pareja.md §6.3): nudge de `nombre_pareja`
 * en el flujo de paciente existente.
 * Feature 2 (§7.4): progreso del mapa de 12 estaciones (lectura pública +
 * override del terapeuta/director).
 *
 * El contrato de las RPC es fijo — coordinado con el agente `db`, migración
 * (`sql/migrations/roadmap_progreso_y_nombre_pareja_2026_09_12.sql`) escrita y
 * pendiente de revisión `security` antes de aplicar a producción:
 *
 *   get_pareja_nudge_state(p_paciente_id uuid, p_rut text) → boolean
 *   set_nombre_pareja_publico(p_paciente_id uuid, p_rut text, p_nombre_pareja text) → void
 *   get_roadmap_progreso_publico(p_paciente_id uuid, p_rut text)
 *     → TABLE(tipo_roadmap text, sesion_actual int, total_sesiones int)
 *   set_roadmap_progreso(p_patient_id uuid, p_therapist_id uuid, p_tipo_roadmap text, p_sesion_actual int)
 *     → jsonb
 *
 * Las 3 primeras son `anon`-callable (booking público, sin sesión) — el chequeo
 * de propiedad (RUT vs. paciente) vive server-side en la RPC, nunca en el
 * cliente. `set_roadmap_progreso` requiere sesión de terapeuta/director
 * (`authenticated`) — el inversor NO tiene rama (plan §3 decisión 7).
 */

import { supabase } from '../lib/supabase/client';

/**
 * Clave de localStorage que marca el nudge como resuelto en este dispositivo
 * (§6.3: "una vez guardado, Konex no dice más"). Se setea al guardar el nombre
 * de la pareja con éxito — evita depender solo del round-trip de la RPC de
 * lectura para que la tarjeta y Konex dejen de insistir de inmediato.
 */
export const PAREJA_NUDGE_LOCALSTORAGE_KEY = 'konex_pareja_nudge';

/**
 * `true` sii al paciente le falta `nombre_pareja` y tiene al menos una consulta
 * de pareja. Ante cualquier error de red/RPC devuelve `false` — el nudge nunca
 * debe bloquear ni entorpecer el flujo de booking.
 */
export async function getParejaNudgeState(pacienteId: string, rut: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('get_pareja_nudge_state', {
      p_paciente_id: pacienteId,
      p_rut: rut,
    });

    if (error) {
      console.error('Error consultando el estado del nudge de pareja');
      return false;
    }

    return data === true;
  } catch {
    console.error('Error inesperado consultando el estado del nudge de pareja');
    return false;
  }
}

/** Guarda (o actualiza) el nombre de la pareja del paciente identificado por RUT. */
export async function saveNombrePareja(
  pacienteId: string,
  rut: string,
  nombre: string
): Promise<{ ok: boolean }> {
  try {
    const { error } = await supabase.rpc('set_nombre_pareja_publico', {
      p_paciente_id: pacienteId,
      p_rut: rut,
      p_nombre_pareja: nombre,
    });

    if (error) {
      console.error('Error guardando el nombre de la pareja');
      return { ok: false };
    }

    return { ok: true };
  } catch {
    console.error('Error inesperado guardando el nombre de la pareja');
    return { ok: false };
  }
}

// ─── Feature 2 — progreso del roadmap de 12 estaciones (plan §7.4) ──────────

export type TipoRoadmap = 'pareja' | 'individual';

export interface RoadmapProgreso {
  tipoRoadmap: TipoRoadmap;
  sesionActual: number;
  totalSesiones: number;
}

/**
 * Progreso público del roadmap — anon-callable, ownership por RUT (server-side).
 * `null` cuando la RPC no devuelve fila: RUT no matchea, paciente sin ninguna
 * consulta `pareja` ni override, o error de red/RPC. El caller (useRoadmapProgreso)
 * trata ese `null` igual que "paciente sin ruta de pareja todavía" — nunca
 * bloquea el mapa público (plan §7.6: el mapa se abre igual en modo preview).
 */
export async function getRoadmapProgreso(
  pacienteId: string,
  rut: string
): Promise<RoadmapProgreso | null> {
  try {
    const { data, error } = await supabase.rpc('get_roadmap_progreso_publico', {
      p_paciente_id: pacienteId,
      p_rut: rut,
    });

    if (error) {
      console.error('Error consultando el progreso del roadmap');
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;

    return {
      tipoRoadmap: row.tipo_roadmap,
      sesionActual: row.sesion_actual,
      totalSesiones: row.total_sesiones,
    };
  } catch {
    console.error('Error inesperado consultando el progreso del roadmap');
    return null;
  }
}

interface ConsultaParaProximaFecha {
  fecha_hora: string;
  tipo_consulta: string;
  estado: string;
}

/**
 * Próxima consulta de pareja agendada del paciente — badge de fecha del mapa
 * v2 (docs/spec_roadmap_pareja_map.md §14). Reusa `get_patient_consultations`
 * (SECURITY DEFINER, ya en producción, `ExistingPatientBooking.tsx:239-240`)
 * — NO es una RPC nueva, solo se filtra/ordena client-side lo que ya trae:
 * `tipo_consulta === 'pareja'`, desde hoy y estado activo (mismo criterio
 * que `ExistingPatientBooking.tsx` usa para "próximas sesiones").
 * `null` sin fila futura de pareja o ante cualquier error — nunca bloquea el mapa.
 * SEC-057: `get_patient_consultations` exige `p_patient_rut` (gate de ownership
 * server-side) — el único caller (`useRoadmapProgreso`) ya tiene el RUT
 * en scope.
 */
export async function getProximaConsultaPareja(pacienteId: string, pacienteRut: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.rpc('get_patient_consultations', {
      p_patient_id: pacienteId,
      p_patient_rut: pacienteRut,
    });

    if (error) {
      console.error('Error consultando la próxima consulta de pareja');
      return null;
    }

    // Desde el inicio de HOY (no desde "ahora"): la consulta de hoy sigue
    // contando como agendada aunque su hora ya haya pasado y el terapeuta
    // todavía no la marque completada — si no, la estación se re-bloquea
    // durante la tarde de la propia sesión.
    const inicioHoy = new Date();
    inicioHoy.setHours(0, 0, 0, 0);
    const desde = inicioHoy.getTime();
    const rows = (data as ConsultaParaProximaFecha[] | null) ?? [];
    const proximas = rows
      .filter(
        (c) =>
          c.tipo_consulta === 'pareja' &&
          new Date(c.fecha_hora).getTime() >= desde &&
          ['programada', 'confirmada', 'en_progreso'].includes(c.estado)
      )
      .sort((a, b) => a.fecha_hora.localeCompare(b.fecha_hora));

    return proximas[0]?.fecha_hora ?? null;
  } catch {
    console.error('Error inesperado consultando la próxima consulta de pareja');
    return null;
  }
}

export interface SetRoadmapProgresoResult {
  ok: boolean;
  data?: { ok: boolean; tipo_roadmap: TipoRoadmap; sesion_actual: number | null; modo: 'auto' | 'manual' | 'automatico' };
}

/**
 * Override manual del terapeuta/director. `sesionActual = null` borra el
 * override (vuelve a modo automático — conteo de consultas completadas).
 * `therapistId = null` es la rama director (plan §7.4/§7.8) — nunca se manda
 * un id de terapeuta "por las dudas" en esa rama, la RPC lo exige `NULL`.
 * Requiere sesión `authenticated` — nunca se llama desde el flujo público.
 */
export async function setRoadmapProgreso(
  patientId: string,
  therapistId: string | null,
  tipoRoadmap: TipoRoadmap,
  sesionActual: number | null
): Promise<SetRoadmapProgresoResult> {
  try {
    const { data, error } = await supabase.rpc('set_roadmap_progreso', {
      p_patient_id: patientId,
      p_therapist_id: therapistId,
      p_tipo_roadmap: tipoRoadmap,
      p_sesion_actual: sesionActual,
    });

    if (error) {
      console.error('Error guardando el progreso del roadmap');
      return { ok: false };
    }

    return { ok: true, data: data ?? undefined };
  } catch {
    console.error('Error inesperado guardando el progreso del roadmap');
    return { ok: false };
  }
}

/**
 * FUNC-129 — ¿el terapeuta/director bloqueó el mapa de la ruta a este
 * paciente? Bloqueado → `get_roadmap_progreso_publico` devuelve 0 filas y el
 * mapa no aparece en `/agendar`. Misma doble rama director/terapeuta dueño que
 * `setRoadmapProgreso` (`therapistId = null` = director). `null` ante error —
 * la UI deja el switch deshabilitado en vez de mostrar un estado inventado.
 */
export async function getRoadmapBloqueo(
  patientId: string,
  therapistId: string | null,
  tipoRoadmap: TipoRoadmap
): Promise<boolean | null> {
  try {
    const { data, error } = await supabase.rpc('get_roadmap_bloqueo', {
      p_patient_id: patientId,
      p_therapist_id: therapistId,
      p_tipo_roadmap: tipoRoadmap,
    });
    if (error) {
      console.error('Error consultando el bloqueo del roadmap');
      return null;
    }
    return data === true;
  } catch {
    console.error('Error inesperado consultando el bloqueo del roadmap');
    return null;
  }
}

export async function setRoadmapBloqueo(
  patientId: string,
  therapistId: string | null,
  tipoRoadmap: TipoRoadmap,
  bloqueado: boolean
): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('set_roadmap_bloqueo', {
      p_patient_id: patientId,
      p_therapist_id: therapistId,
      p_tipo_roadmap: tipoRoadmap,
      p_bloqueado: bloqueado,
    });
    if (error) {
      console.error('Error guardando el bloqueo del roadmap');
      return false;
    }
    return true;
  } catch {
    console.error('Error inesperado guardando el bloqueo del roadmap');
    return false;
  }
}
